import type { SelectChangeEvent } from "@mui/material";
import { MenuItem, Select, TextField } from "@mui/material";
import type { TextObjectFilter, TextObjectFilterOperator } from "../../api/types/objectTypes";
import { allTextObjectFilterOperators } from "../../api/types/objectTypes";
import { textFilterOperatorCaptions } from "./objectFilterHelpers";

interface Props {
  filter: TextObjectFilter;
  onFilterChange: (filter: TextObjectFilter) => void;
}

const operatorToValueControlTypeMap: Record<TextObjectFilterOperator, "none" | "single" | "array"> = {
  Contains: "single",
  DoesNotContain: "single",
  EqualsCaseInsensitive: "single",
  InArray: "array",
  Empty: "none",
  NotEmpty: "none",
};

const ObjectTextFilterEditorContent = ({ filter, onFilterChange }: Props) => {
  const handleFilterOperatorChange = (event: SelectChangeEvent<string>) => {
    const newOperator = event.target.value as TextObjectFilterOperator;
    const controlType = operatorToValueControlTypeMap[newOperator];
    onFilterChange({
      operator: newOperator,
      value: controlType === "single" ? filter.value : undefined,
      values: controlType === "array" ? filter.values : undefined,
    });
  };

  const handleValueChange = (value: string) => {
    onFilterChange({ ...filter, value });
  };

  const handleValuesChange = (value: string) => {
    const values = value
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
    onFilterChange({ ...filter, values });
  };

  const valueControlType = operatorToValueControlTypeMap[filter.operator];

  return (
    <>
      <Select value={filter.operator} onChange={handleFilterOperatorChange} sx={{ flex: "0 0 12.5rem" }}>
        {allTextObjectFilterOperators.map((operator) => (
          <MenuItem key={operator} value={operator}>
            {textFilterOperatorCaptions[operator]}
          </MenuItem>
        ))}
      </Select>

      {valueControlType === "single" && (
        <TextField
          value={filter.value ?? ""}
          onChange={(e) => handleValueChange(e.target.value)}
          sx={{ flexGrow: 1 }}
        />
      )}

      {valueControlType === "array" && (
        <TextField
          value={filter.values?.join(", ") ?? ""}
          onChange={(e) => handleValuesChange(e.target.value)}
          placeholder="Comma-separated values"
          sx={{ flexGrow: 1 }}
        />
      )}
    </>
  );
};

export default ObjectTextFilterEditorContent;
